import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../components/Button';
import { products } from '../data/products';

export const ProductDetail = () => {
  const { slug } = useParams();

  const product = useMemo(
    () => products.find((item) => item.slug === slug),
    [slug]
  );

  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setSelectedImage(null);
  }, [slug]);

  if (!product) {
    return (
      <div style={{ marginTop: 80, padding: '6rem 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1>Produto não encontrado</h1>
          <Button to="/solucoes">Voltar para Soluções</Button>
        </div>
      </div>
    );
  }

  const gallery =
    product.gallery && product.gallery.length > 0
      ? product.gallery
      : [product.image].filter(Boolean);

  const activeImage = selectedImage || gallery[0];

  return (
    <div style={{ marginTop: 80 }}>
      <section
        style={{
          padding: '1.1rem 0',
          background: '#020617',
          color: '#cbd5e1',
        }}
      >
        <div className="container">
          <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
            <Link to="/" style={{ color: '#cbd5e1', textDecoration: 'none' }}>
              Home
            </Link>
            <span>›</span>
            <Link to="/solucoes" style={{ color: '#cbd5e1', textDecoration: 'none' }}>
              Soluções
            </Link>
            <span>›</span>
            <strong style={{ color: '#fff' }}>{product.title}</strong>
          </div>
        </div>
      </section>

      <section style={{ padding: '4rem 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div
            className="product-detail-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.05fr .95fr',
              gap: '3rem',
              alignItems: 'start',
            }}
          >
            <div>
              <motion.div
                key={activeImage || product.slug}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                style={{
                  height: 390,
                  borderRadius: 26,
                  background: `linear-gradient(135deg, ${product.color}22, rgba(99,102,241,.12))`,
                  border: '1px solid rgba(148,163,184,.18)',
                  overflow: 'hidden',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {activeImage ? (
                  <img
                    src={activeImage}
                    alt={product.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <i
                    className={product.icon}
                    style={{
                      fontSize: '6rem',
                      color: product.color,
                    }}
                  />
                )}
              </motion.div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1rem',
                  marginTop: '1rem',
                }}
              >
                {(gallery.length ? gallery : [null, null, null]).slice(0, 3).map((img, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => img && setSelectedImage(img)}
                    style={{
                      height: 118,
                      borderRadius: 18,
                      border:
                        img === activeImage
                          ? `2px solid ${product.color}`
                          : '1px solid rgba(148,163,184,.18)',
                      background: `linear-gradient(135deg, ${product.color}22, rgba(99,102,241,.10))`,
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={`${product.title} ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <i
                        className={product.icon}
                        style={{
                          fontSize: '2.4rem',
                          color: product.color,
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span
                style={{
                  display: 'inline-flex',
                  padding: '.45rem .85rem',
                  borderRadius: '999px',
                  background: `${product.color}22`,
                  color: product.color,
                  fontSize: '.78rem',
                  fontWeight: 900,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  marginBottom: '1rem',
                }}
              >
                {product.tag}
              </span>

              <h1
                style={{
                  color: 'var(--dark)',
                  fontSize: 'clamp(2.4rem, 5vw, 3.5rem)',
                  lineHeight: 1.05,
                  marginBottom: '1rem',
                }}
              >
                {product.title}
              </h1>

              <p
                style={{
                  color: 'var(--gray)',
                  lineHeight: 1.8,
                  fontSize: '1.08rem',
                  marginBottom: '1.5rem',
                }}
              >
                {product.short}
              </p>

              <div
                style={{
                  display: 'flex',
                  gap: '.75rem',
                  flexWrap: 'wrap',
                  marginBottom: '2rem',
                }}
              >
                {product.benefits.map((benefit) => (
                  <span
                    key={benefit}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '.5rem',
                      padding: '.8rem 1rem',
                      borderRadius: 14,
                      background: `${product.color}14`,
                      border: `1px solid ${product.color}26`,
                      color: product.color,
                      fontWeight: 800,
                    }}
                  >
                    <i className="fas fa-check-circle" />
                    {benefit}
                  </span>
                ))}
              </div>

              <div
                style={{
                  padding: '1.5rem 0',
                  borderTop: '1px solid rgba(148,163,184,.22)',
                  borderBottom: '1px solid rgba(148,163,184,.22)',
                  marginBottom: '1.8rem',
                  display: 'grid',
                  gap: '.9rem',
                }}
              >
                {product.details.map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '120px 1fr',
                      gap: '1rem',
                      color: 'var(--dark)',
                    }}
                  >
                    <strong>{label}:</strong>
                    <span style={{ color: 'var(--gray)' }}>{value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Button to={`/planos/${product.slug}`}>
                  <i className="fas fa-gift" /> Ver Planos
                </Button>

                <Button variant="secondary" to="/contato">
                  <i className="fab fa-whatsapp" /> Agendar Demonstração
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '5rem 0',
          background:
            `linear-gradient(135deg, ${product.color}10, rgba(99,102,241,.06))`,
        }}
      >
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2
              style={{
                color: 'var(--dark)',
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                marginBottom: '.75rem',
              }}
            >
              Principais Recursos
            </h2>
            <p style={{ color: 'var(--gray)', fontSize: '1.08rem' }}>
              Tudo o que você precisa para operar com mais controle.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.25rem',
            }}
          >
            {product.features.map(([icon, title, desc]) => (
              <motion.div
                key={title}
                whileHover={{ y: -6 }}
                style={{
                  padding: '1.5rem',
                  borderRadius: 22,
                  background: 'var(--bg-primary)',
                  border: '1px solid rgba(148,163,184,.18)',
                  boxShadow: '0 18px 45px rgba(15,23,42,.07)',
                  display: 'flex',
                  gap: '1rem',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 15,
                    background: `${product.color}18`,
                    color: product.color,
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <i className={icon} />
                </div>

                <div>
                  <h3 style={{ color: 'var(--dark)', marginBottom: '.45rem' }}>
                    {title}
                  </h3>
                  <p style={{ color: 'var(--gray)', lineHeight: 1.6, margin: 0 }}>
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '4.5rem 0', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div
            style={{
              borderRadius: 30,
              padding: '3rem',
              background: `linear-gradient(135deg, ${product.color}18, rgba(99,102,241,.08))`,
              border: `1px solid ${product.color}33`,
              display: 'grid',
              gridTemplateColumns: '90px 1fr auto',
              gap: '2rem',
              alignItems: 'center',
            }}
            className="product-cta"
          >
            <div
              style={{
                width: 78,
                height: 78,
                borderRadius: '50%',
                background: product.color,
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontSize: '2rem',
              }}
            >
              <i className={product.icon} />
            </div>

            <div>
              <h2 style={{ color: 'var(--dark)', marginBottom: '.5rem' }}>
                Pronto para conhecer o {product.title}?
              </h2>
              <p style={{ color: 'var(--gray)', lineHeight: 1.7, margin: 0 }}>
                Agende uma demonstração gratuita e veja na prática como essa
                solução pode ajudar sua operação.
              </p>
            </div>

            <Button to="/contato">
              <i className="fab fa-whatsapp" /> Agendar Demonstração
            </Button>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .product-detail-grid {
            grid-template-columns: 1fr !important;
          }

          .product-cta {
            grid-template-columns: 1fr !important;
            text-align: center;
          }

          .product-cta > div:first-child {
            margin: 0 auto;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;